import React, {useEffect, useMemo, useState} from "react";
import classnames from "classnames";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../../hooks/use-is-mounted";
import SaveOrRollback from "../../save-or-rollback";
import {Box, Fade, Tooltip} from "@mui/material";
import {Edit} from "@mui/icons-material";
import IconButton from "../../../base/button/icon_button";
import CopyIconButton from "../../../base/copy-to-clipboard/copy-icon-button";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from '../../../form';
import DateUtils from "../../../../../core/services/date-utils";


export const summaryInfoCardBodyEntryTypes = {
    text: "text",
    date: "date",
}

/**
 * Creates the name identifier of the body entry to be used in th form entities.
 * @param {any} id
 * @return {`summary-info-card-${string}`}
 */
const nameIdentifier = (id) => `summary-info-card-${id}`;


const SummaryInfoCard = ({header, body = [], className = '', style = {}, loading, loadingCount = 4}) => {
    const [inEditMode, setInEditMode] = useState([]);
    const [submitting, setSubmitting] = useState([]);
    const isMounted = useIsMounted();

    const schema = useMemo(() => {
        const res = {};
        for (const entry of body) {
            const name = nameIdentifier(entry.id);
            switch (entry.type) {
                case summaryInfoCardBodyEntryTypes.date:
                    res[name] = Yup.mixed().validDate(ValidateMessages.incorrect).nullable();
                    break
                default:
                    res[name] = Yup.string().nullable();
                    break;
            }

        }
        return Yup.object().shape(res);
    }, [body])

    const initialValues = useMemo(() => {
        const res = {};
        for (const entry of body) {
            switch (entry.type) {
                case summaryInfoCardBodyEntryTypes.date: {
                    const name = nameIdentifier(entry.id);
                    res[name] = !inEditMode.find(e => entry.id === e)
                        ? entry.value ? DateUtils.serverToUTC(entry.value) : undefined
                        : entry.value ? DateUtils.serverToUTC(entry.value) : undefined
                    break;
                }
                default: {
                    const name = nameIdentifier(entry.id);
                    res[name] = !inEditMode.find(e => entry.id === e) && entry.format
                        ? entry.format(entry.value)
                        : entry.value?.toString();
                    break;
                }
            }
        }
        return res;
    }, [body, inEditMode])

    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    /**
     * With each change in the body entries:
     * - removes the non-existing yet still submitting entries from the state
     * - removes the non-existing yet still in edit mode entries from the state
     */
    useEffect(() => {
        setSubmitting(prevState => {
            const newIds = body?.map(e => e.id) ?? [];
            return prevState?.filter(e => newIds.includes(e));
        })
        setInEditMode(prevState => {
            const newIds = body?.map(e => e.id) ?? [];
            return prevState?.filter(e => newIds.includes(e));
        })
    }, [body])

    /**
     * Edits the entry value by calling its respective edit callback.
     *
     * * if the entry is not clearable and the value is an empty string, then just toggles the edit mode to bring it
     * to view mode
     * * if the result flag of the edit callback is successful, toggles the edit mode to bring it
     * to view mode
     * @param value
     * @param entry
     * @return {Promise<void>}
     */
    const editEntryValue = async (value, entry) => {
        if (entry.type === summaryInfoCardBodyEntryTypes.date) {
            value = value ? DateUtils.UTCToServer(value).format(DateUtils.withoutOffsetFormat) : value;
        }
        if (!entry?.clearable && !value?.toString()?.trim()) {
            toggleInEditMode(entry.id);
            return;
        }
        toggleSubmitting(entry.id);
        const resultFlag = await entry?.edit(entry, value);
        if (!isMounted())
            return;
        toggleSubmitting(entry.id);

        if (resultFlag && !entry?.openDialog) {
            toggleInEditMode(entry.id);
        }
    }

    /**
     * Either appends or removes the given entry id to or from the [inEditMode] state
     * @param {any} id
     */
    const toggleInEditMode = (id) => {
        setInEditMode(prevState => {
            if (prevState?.includes(id)) {
                return prevState.filter(e => e !== id);
            }
            return [...(prevState ?? []), id];
        })
    }

    /**
     * Either appends or removes the given entry id to or from the [submitting] state
     * @param {any} id
     */
    const toggleSubmitting = (id) => {
        setSubmitting(prevState => {
            if (prevState?.includes(id)) {
                return prevState.filter(e => e !== id);
            }
            return [...(prevState ?? []), id];
        })
    }

    /**
     * Changes the form input's value to its initial value and then toggles the entry back to view mode.
     * @param entry
     * @param form
     */
    const rollbackChanges = (entry, form) => {
        const name = nameIdentifier(entry.id);
        form.change(name, initialValues[name]);
        form.resetFieldState(name);
        toggleInEditMode(entry.id);
    }

    const titleValue = loading
        ? header?.loadingValue ?? ''
        : header?.format
            ? header?.format(header?.value) ?? ''
            : header?.value ?? '';

    return (
        <>
            <div
                style={style}
                className={classnames('summary-info-card', className)}
            >
                {!!header &&
                    <div className={'summary-info-card-header'}>
                        <p className={'summary-info-card-header-title'}>
                            {header?.title ?? ''}:
                        </p>
                        {
                            !!titleValue &&
                            <p className={'summary-info-card-header-value'}>
                                {
                                    titleValue
                                }
                                {
                                    header?.copyable && !loading &&
                                    <CopyIconButton value={header?.value}/>
                                }
                            </p>
                        }
                    </div>
                }
                <Form
                    onSubmit={() => false}
                    initialValues={initialValues}
                    validate={validate}
                    formProps={{className: 'summary-info-card-body',}}
                >
                    {({form, initialValues, values}) => (
                        <>
                            {
                                loading
                                    ? Array(loadingCount).fill(null).map((_, e) => (
                                        <div key={e} className={'loading extraSmall mt-1'}>
                                            <div/>
                                        </div>
                                    ))
                                    : body?.map(entry => {
                                        const name = nameIdentifier(entry?.id);
                                        const submittingEntry = !!submitting?.find(e => e === entry?.id);
                                        const entryIsInEditMode = !!inEditMode?.find(e => e === entry?.id);

                                        let valueChanged;
                                        switch (entry.type) {
                                            case summaryInfoCardBodyEntryTypes.date:
                                                valueChanged = initialValues[name] !== values[name] && ((entry?.clearable && (!values[name] || DateUtils.server(values[name]).isValid())) || (!entry?.clearable && DateUtils.server(values[name]).isValid()))
                                                break;
                                            default:
                                                valueChanged = initialValues[name] !== values[name];
                                                break;
                                        }
                                        return (

                                            <div key={entry?.id} className={classnames('summary-info-card-body-entry', {[entry?.className]: true})}>
                                                <div className={'summary-info-card-body-entry-title'}>
                                                    {
                                                        entry?.titlePrefix
                                                    }
                                                    <div className={'summary-info-card-body-entry-title-text'}>
                                                        {entry?.title ?? ''}{entry?.hideValue ? '' : ':'}
                                                    </div>
                                                    {
                                                        entry?.titleSuffix
                                                    }
                                                    {
                                                        entry?.editable &&
                                                        <Fade in={!entryIsInEditMode && !submittingEntry}>
                                                            <IconButton
                                                                disabled={submittingEntry}
                                                                className={'mx-1'}
                                                                color={'secondary'}
                                                                onPressed={() => entry.openDialog ? editEntryValue(entry.value, entry) : toggleInEditMode(entry.id)}
                                                            >
                                                                <Edit className={'icon small'}/>
                                                            </IconButton>
                                                        </Fade>
                                                    }
                                                    {
                                                        entry?.copyable &&
                                                        <CopyIconButton value={entry?.value}/>
                                                    }
                                                </div>
                                                {
                                                    !entry?.hideValue &&
                                                    <div className={classnames('summary-info-card-body-entry-value')}>
                                                        <SaveOrRollback
                                                            column={false}
                                                            showText={false}
                                                            show={{
                                                                save: entryIsInEditMode && valueChanged,
                                                                cancel: entryIsInEditMode,
                                                            }}
                                                            onRollback={() => rollbackChanges(entry, form)}
                                                            loading={submittingEntry}
                                                            onSave={() => editEntryValue(values[name], entry)}
                                                        />
                                                        <Tooltip
                                                            arrow
                                                            placement={'left'}
                                                            disableHoverListener={entryIsInEditMode}
                                                            disableFocusListener={entryIsInEditMode}
                                                            disableTouchListener={entryIsInEditMode}
                                                            title={
                                                                entry?.format
                                                                    ? entry?.format(entry?.value) ?? ''
                                                                    : entry?.value ?? ''
                                                            }
                                                        >
                                                            <Box component={'span'}>
                                                                {
                                                                    (!entry.type || entry.type === summaryInfoCardBodyEntryTypes.text) &&
                                                                    <MuiRFF.TextField
                                                                        fullWidth={false}
                                                                        name={name}
                                                                        size={"small"}
                                                                        placeholder={entryIsInEditMode ? "type..." : '--'}
                                                                        required={required[name]}
                                                                        disabled={submittingEntry || !entryIsInEditMode}
                                                                        className={classnames({'edit-mode': entryIsInEditMode})}
                                                                        InputProps={{
                                                                            startAdornment: entry?.startAdornment,
                                                                            endAdornment: entry?.endAdornment,
                                                                        }}
                                                                    />
                                                                }
                                                                {
                                                                    entry.type === summaryInfoCardBodyEntryTypes.date &&
                                                                    <MuiRFF.DatePicker
                                                                        name={name}
                                                                        required={required[name]}
                                                                        disabled={submittingEntry || !entryIsInEditMode}
                                                                        className={classnames({'edit-mode': entryIsInEditMode})}
                                                                        textFieldProps={{
                                                                            size: 'small',
                                                                            InputProps: {
                                                                                startAdornment: entry?.startAdornment,
                                                                                endAdornment: entry?.endAdornment,
                                                                            },
                                                                        }}
                                                                    />
                                                                }
                                                            </Box>
                                                        </Tooltip>
                                                    </div>
                                                }
                                            </div>
                                        );
                                    })
                            }
                        </>
                    )}
                </Form>
            </div>
        </>
    )
}

export default SummaryInfoCard;
